import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, ProtectedRoute, useAuth, homePathForRole } from '@/lib/auth';
import { ThemeProvider } from '@/lib/theme';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageLoader } from '@/components/PageLoader';
import { CookieBanner } from '@/components/CookieBanner';

// Eager: páginas de entrada (login, registro, invite) — el LCP del primer
// load apunta acá, no queremos un chunk extra de network antes de paint.
import LoginPage from '@/pages/Login';
import RegisterIndividualPage from '@/pages/RegisterIndividual';
import InviteAcceptPage from '@/pages/InviteAccept';
import ForgotPasswordPage from '@/pages/ForgotPassword';
import ResetPasswordPage from '@/pages/ResetPassword';

const PrivacyPage = lazy(() => import('@/pages/Privacy'));
const CookiesPage = lazy(() => import('@/pages/Cookies'));

// Lazy: el resto de páginas se code-splittea por chunk. Reduce bundle inicial
// (antes ~327 KB monolítico) y prioriza el shell/login.
const ProfilePage = lazy(() => import('@/pages/Profile'));

const EstudianteDashboard  = lazy(() => import('@/pages/estudiante/Dashboard'));
const EstudiantePortafolio = lazy(() => import('@/pages/estudiante/Portafolio'));
const EstudianteMiRuta     = lazy(() => import('@/pages/estudiante/MiRuta'));
const EstudianteBitacora   = lazy(() => import('@/pages/estudiante/Bitacora'));
const EstudianteDatosPersonales = lazy(() => import('@/pages/estudiante/DatosPersonales'));

const ProfesorDashboard     = lazy(() => import('@/pages/profesor/Dashboard'));
const ProfesorAlumnos       = lazy(() => import('@/pages/profesor/Alumnos'));
const ProfesorCursos        = lazy(() => import('@/pages/profesor/Cursos'));
const ProfesorObservaciones = lazy(() => import('@/pages/profesor/Observaciones'));
const ProfesorEnPractica    = lazy(() => import('@/pages/profesor/EnPractica'));
const InformePage           = lazy(() => import('@/pages/Informe'));

const MaestroGuiaDashboard   = lazy(() => import('@/pages/maestro-guia/Dashboard'));
const MaestroGuiaAsistencia  = lazy(() => import('@/pages/maestro-guia/Asistencia'));
const MaestroGuiaAvance      = lazy(() => import('@/pages/maestro-guia/Avance'));
const MaestroGuiaObservacion = lazy(() => import('@/pages/maestro-guia/Observacion'));
const MaestroGuiaEvaluacion  = lazy(() => import('@/pages/maestro-guia/Evaluacion'));
const MaestroGuiaBitacora    = lazy(() => import('@/pages/maestro-guia/Bitacora'));
const MaestroGuiaContacto    = lazy(() => import('@/pages/maestro-guia/Contacto'));

const AdminDashboard     = lazy(() => import('@/pages/admin/Dashboard'));
const AdminInvitaciones  = lazy(() => import('@/pages/admin/Invitaciones'));
const AdminUsuarios      = lazy(() => import('@/pages/admin/Usuarios'));
const AdminCursos        = lazy(() => import('@/pages/admin/Cursos'));
const AdminEmpresas      = lazy(() => import('@/pages/admin/Empresas'));
const AdminEvaluaciones  = lazy(() => import('@/pages/admin/Evaluaciones'));
const AdminLogros        = lazy(() => import('@/pages/admin/Logros'));
const AdminRubricas      = lazy(() => import('@/pages/admin/Rubricas'));
const AdminAnalytics     = lazy(() => import('@/pages/admin/Analytics'));
const AdminSkills        = lazy(() => import('@/pages/admin/Skills'));
const AdminMfa           = lazy(() => import('@/pages/admin/Mfa'));
const AsignarLogros      = lazy(() => import('@/pages/AsignarLogros'));

const SuperAdminDashboard = lazy(() => import('@/pages/super-admin/Dashboard'));
const SuperAdminLiceos    = lazy(() => import('@/pages/super-admin/Liceos'));
const SuperAdminUsuarios  = lazy(() => import('@/pages/super-admin/Usuarios'));
const SuperAdminErrores   = lazy(() => import('@/pages/super-admin/Errores'));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
                const status = (error as { status?: number })?.status;
                if (status && [401, 403, 404].includes(status)) return false;
                return failureCount < 2;
            },
        },
    },
});

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    return <Navigate to={user ? homePathForRole(user.role) : '/login'} replace />;
}

export default function App() {
    return (
        <ErrorBoundary>
        <ThemeProvider>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <Suspense fallback={<PageLoader />}>
                    <Routes>
                        {/* Public */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/registro-individual" element={<RegisterIndividualPage />} />
                        <Route path="/invite" element={<InviteAcceptPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/privacidad" element={<PrivacyPage />} />
                        <Route path="/cookies" element={<CookiesPage />} />

                        {/* Estudiante */}
                        <Route path="/estudiante/dashboard" element={
                            <ProtectedRoute roles={['estudiante']}>
                                <AppShell><EstudianteDashboard /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/estudiante/portafolio" element={
                            <ProtectedRoute roles={['estudiante']}>
                                <AppShell><EstudiantePortafolio /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/estudiante/mi-ruta" element={
                            <ProtectedRoute roles={['estudiante']}>
                                <AppShell><EstudianteMiRuta /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/estudiante/bitacora" element={
                            <ProtectedRoute roles={['estudiante']}>
                                <AppShell><EstudianteBitacora /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/estudiante/datos-personales" element={
                            <ProtectedRoute roles={['estudiante']}>
                                <AppShell><EstudianteDatosPersonales /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/estudiante/perfil" element={
                            <ProtectedRoute roles={['estudiante']}>
                                <AppShell><ProfilePage /></AppShell>
                            </ProtectedRoute>
                        } />

                        {/* Profesor */}
                        <Route path="/profesor/dashboard" element={
                            <ProtectedRoute roles={['profesor', 'administrador', 'super_admin']}>
                                <AppShell><ProfesorDashboard /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/profesor/alumnos" element={
                            <ProtectedRoute roles={['profesor', 'administrador', 'super_admin']}>
                                <AppShell><ProfesorAlumnos /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/profesor/cursos" element={
                            <ProtectedRoute roles={['profesor', 'administrador', 'super_admin']}>
                                <AppShell><ProfesorCursos /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/profesor/observaciones" element={
                            <ProtectedRoute roles={['profesor', 'administrador', 'super_admin']}>
                                <AppShell><ProfesorObservaciones /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/profesor/en-practica" element={
                            <ProtectedRoute roles={['profesor', 'administrador', 'super_admin']}>
                                <AppShell><ProfesorEnPractica /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/informe/:studentId" element={
                            <ProtectedRoute roles={['profesor', 'maestro_guia', 'administrador', 'super_admin']}>
                                <InformePage />
                            </ProtectedRoute>
                        } />
                        <Route path="/profesor/perfil" element={
                            <ProtectedRoute roles={['profesor']}>
                                <AppShell><ProfilePage /></AppShell>
                            </ProtectedRoute>
                        } />

                        {/* Maestro Guía */}
                        <Route path="/maestro-guia/dashboard" element={
                            <ProtectedRoute roles={['maestro_guia', 'administrador', 'super_admin']}>
                                <AppShell><MaestroGuiaDashboard /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/asistencia" element={
                            <ProtectedRoute roles={['maestro_guia', 'administrador', 'super_admin']}>
                                <AppShell><MaestroGuiaAsistencia /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/bitacora" element={
                            <ProtectedRoute roles={['maestro_guia', 'administrador', 'super_admin']}>
                                <AppShell><MaestroGuiaBitacora /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/contacto" element={
                            <ProtectedRoute roles={['maestro_guia', 'administrador', 'super_admin']}>
                                <AppShell><MaestroGuiaContacto /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/logros" element={
                            <ProtectedRoute roles={['maestro_guia', 'profesor', 'administrador', 'super_admin']}>
                                <AppShell><AsignarLogros /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/profesor/logros" element={
                            <ProtectedRoute roles={['profesor', 'administrador', 'super_admin']}>
                                <AppShell><AsignarLogros /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/avance" element={
                            <ProtectedRoute roles={['maestro_guia', 'administrador', 'super_admin']}>
                                <AppShell><MaestroGuiaAvance /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/observacion" element={
                            <ProtectedRoute roles={['maestro_guia', 'administrador', 'super_admin']}>
                                <AppShell><MaestroGuiaObservacion /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/evaluacion" element={
                            <ProtectedRoute roles={['maestro_guia', 'administrador', 'super_admin']}>
                                <AppShell><MaestroGuiaEvaluacion /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/maestro-guia/perfil" element={
                            <ProtectedRoute roles={['maestro_guia']}>
                                <AppShell><ProfilePage /></AppShell>
                            </ProtectedRoute>
                        } />

                        {/* Admin */}
                        <Route path="/admin/dashboard" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminDashboard /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/usuarios" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminUsuarios /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/invitaciones" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminInvitaciones /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/cursos" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminCursos /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/empresas" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminEmpresas /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/evaluaciones" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminEvaluaciones /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/logros" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminLogros /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/rubricas" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminRubricas /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/analytics" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminAnalytics /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/skills" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminSkills /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/mfa" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><AdminMfa /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/perfil" element={
                            <ProtectedRoute roles={['administrador', 'super_admin']}>
                                <AppShell><ProfilePage /></AppShell>
                            </ProtectedRoute>
                        } />

                        {/* Super Admin */}
                        <Route path="/super-admin/dashboard" element={
                            <ProtectedRoute roles={['super_admin']}>
                                <AppShell><SuperAdminDashboard /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/super-admin/liceos" element={
                            <ProtectedRoute roles={['super_admin']}>
                                <AppShell><SuperAdminLiceos /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/super-admin/usuarios" element={
                            <ProtectedRoute roles={['super_admin']}>
                                <AppShell><SuperAdminUsuarios /></AppShell>
                            </ProtectedRoute>
                        } />
                        <Route path="/super-admin/errores" element={
                            <ProtectedRoute roles={['super_admin']}>
                                <AppShell><SuperAdminErrores /></AppShell>
                            </ProtectedRoute>
                        } />

                        {/* Root + 404 */}
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="*" element={<RootRedirect />} />
                    </Routes>
                    </Suspense>
                    <CookieBanner />
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
        </ThemeProvider>
        </ErrorBoundary>
    );
}
